// Mapping a single-medication care-plan entry to a discharge-summary
// medication row.
//
// Extracted from DischargeSummaryModal's fetchAndMergeCarePlans so the key
// mapping can be tested — it was silently wrong, and nothing caught it because
// the failure mode is an EMPTY section rather than an error.
//
// Three medication shapes are live in care_plan_data at once:
//   - doctor console   → medication.{drug_name, duration, duration_unit, ...}
//   - voice assistant  → medication.{name, period, periodUnit, ...}
//   - manual modal     → medications[] (an array — handled by its own branch,
//                        which already reads drug_name and is not touched here)
// The reader only ever knew the second one, so every doctor-console-ordered
// medicine failed the `if (name)` guard and never reached the summary.
// CarePlanActivityModal's own table renderer reads `med.drug_name || med.name`
// and `med.duration` / `med.duration_unit` — this matches it.

/**
 * @param {object} data - one care_plan_data row's `data` object.
 * @returns {object|null} a discharge-summary medication row, or null when the
 *   entry names no drug (nothing blank should be pushed into the summary).
 */
export function mapCarePlanMedication(data) {
  const med = (data && data.medication) || {};
  const name = String(
    med.drug_name || med.name || med.drugName || data?.drugName || data?.name || ""
  ).trim();
  if (!name) return null;

  return {
    name,
    // The doctor console captures per-slot counts (morning/afternoon/evening/
    // night) rather than a single dose string, so this stays blank for its
    // entries — an honest blank, not an invented notation.
    dose: String(med.dose || data?.dose || ""),
    frequency: String(med.frequency || data?.frequency || ""),
    start: String(med.start || data?.start || ""),
    period: String(med.duration || med.period || data?.period || ""),
    periodUnit: String(med.duration_unit || med.periodUnit || data?.periodUnit || ""),
    purpose: String(data?.reason || data?.indication || data?.category || ""),
  };
}
